import { ChangeDetectionStrategy, Component, Injector, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';
import { ItemsListContainerBase } from '@fi-app/components/items-list-container-base';
import { Statistic2Service } from '@fi-app/statistic2/statistic2.service';
import { Statistic2ReportParameterListData } from '@fi-app/statistic2/statistic2-report-parameter-list/statistic2-report-parameter-list-data';
import { ListData } from '@fi-app/components/items-list/list-data.service';
import { IStatistic2ReportParameter } from '@fi-app/statistic2/statistic2';
import { AppTextService } from '@fi-shared/app-text';
import { Statistic2HelperService } from '@fi-app/statistic2/statistic2-helper.service';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { FiStateNames } from '@fi-shared/fi-state-proxy/fi-state-names';
import { TabsModuleService } from '@fi-app/utilities/tabs/tabs-module.service';
import { IItemsListDataFetchResult, IItemsListParameterBase } from '@fi-shared/fi-items-list/fi-items-list';

// TODO: delete when feature is 'Stable done' - https://chainformation.atlassian.net/browse/FI-6957
@Component({
    selector: 'fi-statistic2-report-parameter-list',
    template: `
        <items-list-actions
            class="fi-absolute fi-padding-left"
            [selectedItems]="selectedItems$ | async"
            [filterType]="ListData.FilterType"
            [itemsListComponent]="itemsListComponent"
        >
        </items-list-actions>

        <fi-items-list #itemsListComponent [module]="itemsListModule" [moduleExactMatch]="true" [settings]="ListData">
        </fi-items-list>
    `,
    styleUrls: ['./statistic2-report-parameter-list.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
    host: { class: 'fi-statistic2-report-parameter-list fi-relative' },
    standalone: false,
})
export class Statistic2ReportParameterListComponent
    extends ItemsListContainerBase<ListData<IStatistic2ReportParameter>>
    implements OnInit, OnDestroy
{
    itemsListModule: string[] = [FiStateNames.Statistic2.Report._name];

    constructor(
        private statistic2Service: Statistic2Service,
        private statistic2HelperService: Statistic2HelperService,
        private injector: Injector,
        private appTextService: AppTextService,
        private tabsModuleService: TabsModuleService,
    ) {
        super();
    }

    ngOnInit() {
        super.onInit(() => new Statistic2ReportParameterListData(this.injector), {
            showDelete: false,
        });

        this.setListDataSettings();
    }

    private setListDataSettings() {
        this.statistic2Service.reportParametersUpdated$.pipe(takeUntil(this.ngOnDestroy$)).subscribe(() => {
            this.ListData.CancelRequests();
            this.ListData.clearData();
            this.ListData.Initialize();
        });

        this.tabsModuleService.setTabsSettings({
            listData: this.ListData,
        });
    }

    protected LoadItems() {
        const fetchingAllData: boolean = this.ListData.fetchingAllData;
        const parameter: IItemsListParameterBase = this.getParameter();
        parameter.FetchParams = {
            FetchItems: true,
            FetchTotalCount: fetchingAllData,
            FetchLightWeightObjects: fetchingAllData,
        };
        return this.statistic2Service
            .getReportParametersListData(parameter)
            .then((response: IItemsListDataFetchResult<any>) => {
                this.ListData.appendData(response.Items);

                if (fetchingAllData) {
                    this.ListData.SetTotalItemsCount(response.TotalCount);

                    const lightWeightObjectCollection = response.LightWeightObjectsForLocalSearch;
                    if (lightWeightObjectCollection) {
                        this.ListData.raiseLightWeightObjectCollectionChanged(lightWeightObjectCollection);
                    }
                }
            });
    }

    private getParameter(): IItemsListParameterBase {
        const parameterModel: IItemsListParameterBase = {};

        Object.assign(parameterModel, this.ListData.getOffsetParams());

        parameterModel.SelectedSort = {
            ColumnName: this.ListData.Sort.Column,
            SortDirection: this.ListData.Sort.Reverse,
        };

        this.ListData.mergeLocalSearchV2Parameter(parameterModel);

        return parameterModel;
    }

    ngOnDestroy$ = new Subject<void>();
    ngOnDestroy() {
        this.ngOnDestroy$.next();
        this.ngOnDestroy$.complete();
        super.ngOnDestroy();
    }
}
